'use client'

import Image from 'next/image'
import { lazy, memo, Suspense, useEffect, useMemo, useRef, useState } from 'react'
import { useInView } from 'react-intersection-observer'

import { stegaClean } from 'next-sanity'

import { cn } from '@/app/utils/style'
import type { Video as VideoType } from '@/sanity.types'
import { urlForImage } from '@/sanity/lib/utils'

// Dynamic imports for players
const VimeoPlayer = lazy(() =>
  import('@/app/components/VideoPlayers/VimeoPlayer').then((mod) => ({ default: mod.VimeoPlayer })),
)

const HLSPlayer = lazy(() =>
  import('@/app/components/VideoPlayers/HLSPlayer').then((mod) => ({ default: mod.HLSPlayer })),
)

const NativePlayer = lazy(() =>
  import('@/app/components/VideoPlayers/NativePlayer').then((mod) => ({
    default: mod.NativePlayer,
  })),
)

export interface VideoProps {
  video?: VideoType
  className?: string
  classNames?: {
    root?: string
    container?: string
    player?: string
    thumbnail?: string
  }
  // Override video settings
  controls?: boolean
  autoplay?: boolean
  loop?: boolean
  muted?: boolean
  // Additional player props
  volume?: number
  playsinline?: boolean
  onReady?: () => void
  onPlay?: () => void
  onPause?: () => void
  onEnded?: () => void
  onError?: () => void
  onTimeUpdate?: () => void
}

export function hasVideo(video?: VideoType): video is VideoType {
  if (!video) return false

  const source = stegaClean(video.source) || ''

  if (source.includes('vimeo') && (video.vimeo?.files?.length || video.vimeo?.link)) return true
  if (source.includes('url') && video.url) return true
  return false
}

// Vimeo's `base_link` is the full-size original; `_<width>` after its `-d` segment returns it scaled to that
// width in the video's own ratio (the API's sized links are padded to 16:9 with black bars)
const VIMEO_FULL_SIZE_SEGMENT = /-d(?=\?|$)/

function vimeoPosterLoader(baseLink: string, videoWidth: number | undefined) {
  return ({ width }: { width: number }) =>
    VIMEO_FULL_SIZE_SEGMENT.test(baseLink)
      ? baseLink.replace(VIMEO_FULL_SIZE_SEGMENT, `-d_${Math.min(width, videoWidth ?? width)}`)
      : baseLink
}

type PlayerType = 'vimeo' | 'hls' | 'native'

function detectPlayerType(url: string, source?: string): PlayerType {
  const cleanSource = stegaClean(source) || ''

  // Check if it's a Vimeo URL or source
  if (
    cleanSource.includes('vimeo') ||
    url.includes('vimeo.com') ||
    url.includes('player.vimeo.com')
  ) {
    // Check if it's an HLS stream from Vimeo
    if (url.includes('.m3u8')) {
      return 'hls'
    }
    return 'vimeo'
  }

  // Check if it's an HLS stream
  if (url.includes('.m3u8') || url.includes('application/x-mpegURL')) {
    return 'hls'
  }

  // Default to native player for MP4, WebM, etc.
  return 'native'
}

function Video({
  video,
  className,
  classNames,
  controls: controlsOverride,
  autoplay: autoplayOverride,
  loop: loopOverride,
  muted: mutedOverride,
  volume,
  playsinline = true,
  onReady: onReadyProp,
  onPlay,
  onPause,
  onEnded,
  onError,
}: VideoProps) {
  const [isReady, setIsReady] = useState(false)
  const [hasStarted, setHasStarted] = useState(false)
  // The players re-initialise when a callback changes identity, so they get stable ones that call the latest props
  const callbacks = useRef({ onReadyProp, onPlay, onPause, onEnded, onError })
  useEffect(() => {
    callbacks.current = { onReadyProp, onPlay, onPause, onEnded, onError }
  })
  const stableHandlers = useMemo(
    () => ({
      onReady: () => {
        setIsReady(true)
        callbacks.current.onReadyProp?.()
      },
      onPlay: () => {
        setHasStarted(true)
        callbacks.current.onPlay?.()
      },
      onPause: () => callbacks.current.onPause?.(),
      onEnded: () => callbacks.current.onEnded?.(),
      onError: (error: Error) => {
        console.error('Video player error:', error)
        callbacks.current.onError?.()
      },
    }),
    [],
  )
  const [inViewRef, inView] = useInView({
    rootMargin: '0px',
    threshold: 0.1,
    triggerOnce: true,
  })

  if (!hasVideo(video)) return null

  // Use video settings or overrides
  const controls = controlsOverride ?? video.controls ?? true
  const autoplay = autoplayOverride ?? video.autoplay ?? false
  const loop = loopOverride ?? video.loop ?? false
  const muted = mutedOverride ?? video.muted ?? autoplay // Autoplay requires muted

  // Get the video URL
  // For Vimeo, prefer HLS stream URL for custom player, fallback to link
  // Note: Using includes() because stega encoding may add invisible characters to strings
  const url = video.source?.includes('vimeo')
    ? video.vimeo?.files?.find((file) => file.quality?.includes('hls'))?.link || video.vimeo?.link
    : video.url

  if (!url) {
    console.error('No video URL found', video)
    return null
  }

  // Detect which player to use
  const playerType = detectPlayerType(url, video.source)

  // Get thumbnail URL - prefer custom thumbnail, then Vimeo thumbnail (sized by Vimeo through the loader)
  const vimeoBaseLink = video.source === 'vimeo' ? video.vimeo?.pictures?.base_link : undefined
  const thumbnailUrl = video.thumbnail?.asset
    ? urlForImage(video.thumbnail)?.width(1280).height(720).fit('crop').url()
    : vimeoBaseLink
  const thumbnailLoader =
    !video.thumbnail?.asset && vimeoBaseLink
      ? vimeoPosterLoader(vimeoBaseLink, video.vimeo?.width)
      : undefined

  // Calculate aspect ratio from Vimeo dimensions or default to 16:9
  const aspectRatio =
    video.source === 'vimeo' && video.vimeo?.width && video.vimeo?.height
      ? video.vimeo.height / video.vimeo.width
      : 9 / 16

  // Standardized player props
  const playerProps = {
    url,
    controls,
    autoplay,
    loop,
    muted,
    volume,
    playsinline,
    ...stableHandlers,
    className: cn('absolute inset-0', classNames?.player),
  }

  // Vimeo player needs background prop
  const vimeoPlayerProps = {
    ...playerProps,
    background: !controls,
  }

  return (
    <div
      ref={inViewRef}
      className={cn('relative block w-full bg-neutral-100', className, classNames?.root)}
    >
      {/* Aspect ratio spacer */}
      <span className="block w-full" style={{ paddingBottom: `${aspectRatio * 100}%` }} />

      {/* Thumbnail - only show before player is ready */}
      {thumbnailUrl && (
        <Image
          src={thumbnailUrl}
          alt={video.vimeo?.name || 'Video thumbnail'}
          fill
          // Vimeo resizes its own JPEG; the image optimizer's re-encode made it look softer
          loader={thumbnailLoader}
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 1280px"
          className={cn(
            'object-cover z-10 transition-opacity duration-300',
            // Paused videos keep the poster: the stream's first frame starts at a low quality level
            hasStarted || (autoplay && isReady) ? 'opacity-0' : 'opacity-100',
            classNames?.thumbnail,
          )}
          priority
          fetchPriority="high"
        />
      )}

      {/* Video player container */}
      <div className={cn('absolute inset-0 z-20', classNames?.container)}>
        {inView && (
          <Suspense fallback={<div className="absolute inset-0 bg-neutral-100" />}>
            {playerType === 'vimeo' && <VimeoPlayer {...vimeoPlayerProps} />}
            {playerType === 'hls' && <HLSPlayer {...playerProps} />}
            {playerType === 'native' && <NativePlayer {...playerProps} />}
          </Suspense>
        )}
      </div>
    </div>
  )
}

export default memo(Video)
